/**
 * Skill Delivery Types - Defines the 8 delivery methods for skills
 * Each delivery type has its own specific sliders and mana system
 */

import { ElementType } from '@shared/types';

// ========================
// Delivery Type Enum
// ========================

export type SkillDeliveryType =
  | 'projectile'  // Mermi - fires one or more projectiles
  | 'aoe'         // Alan - area of effect damage
  | 'melee'       // Yakin - close range attack
  | 'beam'        // Isin - continuous beam (channeled)
  | 'aura'        // Aura - persistent effect around player (mana reserve)
  | 'buff'        // Buff - temporary self-enhancement
  | 'trap'        // Tuzak - placed trap that triggers on proximity
  | 'totem'       // Totem - placed entity that attacks enemies
  | 'curse'       // Lanet - debuff on enemies
  | 'minion';     // Cagirma - summon persistent follower (mana reserve)

// ========================
// Delivery Type Metadata
// ========================

export interface DeliveryTypeInfo {
  id: SkillDeliveryType;
  name: string;
  nameKey: string;
  description: string;
  descriptionKey: string;
  icon: string;
  color: string;
  manaSystemType: 'normal' | 'channeling' | 'reserve';
}

export const DELIVERY_TYPE_INFO: Record<SkillDeliveryType, DeliveryTypeInfo> = {
  projectile: {
    id: 'projectile',
    name: 'Mermi',
    nameKey: 'delivery.projectile.name',
    description: 'Bir veya daha fazla mermi fırlatır',
    descriptionKey: 'delivery.projectile.description',
    icon: '🏹',
    color: '#3498db',
    manaSystemType: 'normal',
  },
  aoe: {
    id: 'aoe',
    name: 'Alan',
    nameKey: 'delivery.aoe.name',
    description: 'Belirli bir alanda hasar verir',
    descriptionKey: 'delivery.aoe.description',
    icon: '💥',
    color: '#e74c3c',
    manaSystemType: 'normal',
  },
  melee: {
    id: 'melee',
    name: 'Yakın',
    nameKey: 'delivery.melee.name',
    description: 'Yakın mesafe saldırısı',
    descriptionKey: 'delivery.melee.description',
    icon: '⚔️',
    color: '#c0392b',
    manaSystemType: 'normal',
  },
  beam: {
    id: 'beam',
    name: 'Işın',
    nameKey: 'delivery.beam.name',
    description: 'Sürekli enerji ışını (kanalize)',
    descriptionKey: 'delivery.beam.description',
    icon: '⚡',
    color: '#9b59b6',
    manaSystemType: 'channeling',
  },
  aura: {
    id: 'aura',
    name: 'Aura',
    nameKey: 'delivery.aura.name',
    description: 'Etrafında sürekli etki alanı (mana rezerv)',
    descriptionKey: 'delivery.aura.description',
    icon: '✨',
    color: '#f39c12',
    manaSystemType: 'reserve',
  },
  buff: {
    id: 'buff',
    name: 'Buff',
    nameKey: 'delivery.buff.name',
    description: 'Geçici güçlendirme',
    descriptionKey: 'delivery.buff.description',
    icon: '🛡️',
    color: '#2ecc71',
    manaSystemType: 'normal',
  },
  trap: {
    id: 'trap',
    name: 'Tuzak',
    nameKey: 'delivery.trap.name',
    description: 'Düşman yaklaşınca tetiklenen tuzak',
    descriptionKey: 'delivery.trap.description',
    icon: '🪤',
    color: '#8b4513',
    manaSystemType: 'normal',
  },
  totem: {
    id: 'totem',
    name: 'Totem',
    nameKey: 'delivery.totem.name',
    description: 'Düşmanlara saldıran totem',
    descriptionKey: 'delivery.totem.description',
    icon: '🗿',
    color: '#16a085',
    manaSystemType: 'normal',
  },
  curse: {
    id: 'curse',
    name: 'Lanet',
    nameKey: 'delivery.curse.name',
    description: 'Düşmana debuff uygular',
    descriptionKey: 'delivery.curse.description',
    icon: '☠️',
    color: '#8e44ad',
    manaSystemType: 'normal',
  },
  minion: {
    id: 'minion',
    name: 'Çağırma',
    nameKey: 'delivery.minion.name',
    description: 'Kalıcı takipçi çağırır',
    descriptionKey: 'delivery.minion.description',
    icon: '👻',
    color: '#27ae60',
    manaSystemType: 'reserve',
  },
};

// ========================
// AOE Shape Type
// ========================

export type AOEShape = 'circle' | 'cone' | 'line' | 'leap';
export type AOETrigger = 'instant' | 'persistent' | 'delayed';
export type AOEPosition = 'self' | 'target';
export type MeleeType = 'strike' | 'thrust';
export type ProjectileContact = 'none' | 'pierce' | 'chain' | 'fork';
export type BeamMode = 'steady' | 'rampup' | 'overcharge';
export type BuffType = 'warcry' | 'rage' | 'shield' | 'leech';
export type TrapTrigger = 'proximity' | 'timed';
export type TotemType = 'projectile' | 'aoe_pulse' | 'beam' | 'taunt';
export type CurseType = 'weaken' | 'fragility' | 'resist_break' | 'slow';
export type CurseTarget = 'area' | 'single';
export type MinionType = 'melee' | 'ranged' | 'caster';

export const AOE_SHAPE_INFO: Record<AOEShape, { name: string; nameKey: string; icon: string; description: string }> = {
  circle: { name: 'Daire', nameKey: 'aoe.shape.circle', icon: '⭕', description: 'Dairesel alan etkisi' },
  cone: { name: 'Koni', nameKey: 'aoe.shape.cone', icon: '🔺', description: 'Koni şeklinde yelpaze' },
  line: { name: 'Çizgi', nameKey: 'aoe.shape.line', icon: '➖', description: 'Düz hat boyunca duvar' },
  leap: { name: 'Sıçrama', nameKey: 'aoe.shape.leap', icon: '⬆', description: 'Hedefe atla, iniş patlaması' },
};

/** All selection option info maps for UI rendering */
export const SKILL_CHOICES = {
  meleeType: {
    strike: { name: 'Darbe', icon: '🗡', desc: 'En yakın düşmana güçlü vuruş' },
    thrust: { name: 'Hamle', icon: '🏃', desc: 'İleri atılıp yoldakilere vur' },
  } as Record<MeleeType, { name: string; icon: string; desc: string }>,

  projectileContact: {
    none: { name: 'Yok', icon: '⬜', desc: 'İlk düşmana çarpınca durur' },
    pierce: { name: 'Delme', icon: '🔷', desc: 'Düşmanlardan geçer (delme sayısı kadar)' },
    chain: { name: 'Zincir', icon: '🔗', desc: 'Yakın düşmana sıçrar' },
    fork: { name: 'Çatal', icon: '🔱', desc: 'İkiye bölünür' },
  } as Record<ProjectileContact, { name: string; icon: string; desc: string }>,

  aoeTrigger: {
    instant: { name: 'Anlık', icon: '💥', desc: 'Anında patlama' },
    persistent: { name: 'Kalıcı', icon: '🔥', desc: 'Yerde kalıcı alan' },
    delayed: { name: 'Gecikmeli', icon: '⏱', desc: 'Gecikmeli patlama' },
  } as Record<AOETrigger, { name: string; icon: string; desc: string }>,

  aoePosition: {
    self: { name: 'Kendinde', icon: '🧍', desc: 'Etrafında' },
    target: { name: 'Hedefte', icon: '🎯', desc: 'Fare konumunda' },
  } as Record<AOEPosition, { name: string; icon: string; desc: string }>,

  beamMode: {
    steady: { name: 'Sabit', icon: '➡', desc: 'Sabit DPS, basılı tut' },
    rampup: { name: 'Artan', icon: '📈', desc: 'Zamanla güçleniyor' },
    overcharge: { name: 'Patlama', icon: '💣', desc: 'Şarj et, bırakınca patlama' },
  } as Record<BeamMode, { name: string; icon: string; desc: string }>,

  buffType: {
    warcry: { name: 'Savaş Çığlığı', icon: '📣', desc: 'Sonraki X saldırı güçlü' },
    rage: { name: 'Öfke', icon: '😡', desc: 'Güç artışı, HP kaybı' },
    shield: { name: 'Koruma', icon: '🛡', desc: 'Geçici kalkan' },
    leech: { name: 'Ruh Bağı', icon: '💜', desc: 'Vuruşlar can/mana çalar' },
  } as Record<BuffType, { name: string; icon: string; desc: string }>,

  trapTrigger: {
    proximity: { name: 'Yakınlık', icon: '👁', desc: 'Düşman yaklaşınca' },
    timed: { name: 'Zamanlı', icon: '⏰', desc: 'X saniye sonra' },
  } as Record<TrapTrigger, { name: string; icon: string; desc: string }>,

  totemType: {
    projectile: { name: 'Mermi', icon: '🏹', desc: 'Mermi atar' },
    aoe_pulse: { name: 'Patlama', icon: '💥', desc: 'Etrafına hasar' },
    beam: { name: 'Işın', icon: '⚡', desc: 'Sürekli ışın' },
    taunt: { name: 'Tahrik', icon: '😤', desc: 'Düşmanları çeker' },
  } as Record<TotemType, { name: string; icon: string; desc: string }>,

  curseType: {
    weaken: { name: 'Zayıflatma', icon: '⬇', desc: 'Düşman az hasar verir' },
    fragility: { name: 'Kırılganlık', icon: '💔', desc: 'Düşman çok hasar alır' },
    resist_break: { name: 'Direnç Kırma', icon: '🔓', desc: 'Dirençleri düşürür' },
    slow: { name: 'Yavaşlatma', icon: '🐌', desc: 'Hareket/saldırı yavaşlar' },
  } as Record<CurseType, { name: string; icon: string; desc: string }>,

  curseTarget: {
    area: { name: 'Alan', icon: '⭕', desc: 'Alandaki düşmanlar' },
    single: { name: 'Tek Hedef', icon: '🎯', desc: 'Tek düşman' },
  } as Record<CurseTarget, { name: string; icon: string; desc: string }>,

  minionType: {
    melee: { name: 'Savaşçı', icon: '⚔', desc: 'Yakın dövüş' },
    ranged: { name: 'Okçu', icon: '🏹', desc: 'Uzak mesafe' },
    caster: { name: 'Büyücü', icon: '🔮', desc: 'Büyü saldırısı' },
  } as Record<MinionType, { name: string; icon: string; desc: string }>,
};

// ========================
// Type-Specific Slider Interfaces
// ========================
// All sliders use concrete values with specific units (px, adet, saniye, etc.)

export interface ProjectileSliders {
  [key: string]: number;
  damage: number;         // 5-40: Flat damage per projectile
  projectileCount: number; // 1-7: Number of projectiles (adet)
  speed: number;          // 200-600: Projectile travel speed (px/s)
  spreadAngle: number;    // 0-360: Spread angle (derece)
  range: number;          // 100-400: Projectile range (px)
  thickness: number;      // 5-25: Projectile thickness (px)
  pierce: number;         // 1-5: Pierce count (adet)
  chain: number;          // 1-5: Chain count (adet)
  forkCount: number;      // 2-5: Fork projectile count (adet)
}

export interface AOESliders {
  [key: string]: number | AOEShape;
  damage: number;         // 10-60: Flat damage
  radius: number;         // 40-200: Area radius (px)
  duration: number;       // 1-5: Duration for persistent AoEs (saniye), step 0.5
  coneAngle: number;      // 30-120: Cone angle (derece) - only for cone shape
  delay: number;          // 0.5-2.0: Delay before impact (saniye) - only for delayed
  range: number;          // 50-300: Cast range (px) - only for targeted/chain
  shape: AOEShape;        // 'circle' or 'cone' - affects area pattern
}

export interface MeleeSliders {
  [key: string]: number;
  damage: number;         // 50-200: Damage percentage of player's attack damage
  range: number;          // 30-80: Attack range (px)
  thrustRange: number;    // 60-250: Thrust dash distance (px), only for thrust meleeType
}

export interface BeamSliders {
  [key: string]: number;
  dps: number;            // 5-40: Flat damage per second
  width: number;          // 5-30: Beam width (px)
  range: number;          // 80-300: Beam length (px)
  manaDrain: number;      // 5-25: Mana drain (mana/s)
}

export interface AuraSliders {
  [key: string]: number;
  effectStrength: number; // 5-25: Effect power (% bonus)
  manaReserve: number;    // 15-50: Mana reserve (% max mana)
}

export interface BuffSliders {
  [key: string]: number;
  effectStrength: number; // Varies by sub-type (see BUFF_SUBTYPE_SLIDER_OVERRIDES)
  charges: number;        // Warcry: 3-8 charges (adet)
  hpCost: number;         // Rage: 1-5 HP cost per second
  duration: number;       // Varies by sub-type (saniye)
  shield: number;         // Koruma: 50-300 shield amount
  shieldDecay: number;    // Koruma: 10-30 decay per second
  leech: number;          // Ruh Bagi: 3-15 leech percent
  cooldown: number;       // Varies by sub-type (saniye)
}

export interface TrapSliders {
  [key: string]: number;
  damage: number;         // 10-80: Flat trap damage
  triggerRadius: number;  // 20-80: Detection radius (px)
  trapCount: number;      // 1-5: Number of traps (adet)
  armTime: number;        // 0.5-3.0: Arm time (saniye), step 0.5
}

export interface TotemSliders {
  [key: string]: number;
  totemHealth: number;    // 50-300: Totem HP (taunt: 100-600)
  damage: number;         // 5-30: Flat damage per hit (hidden for taunt)
  range: number;          // 60-200: Totem attack range (px)
}

export interface CurseSliders {
  [key: string]: number;
  power: number;          // 5-30, step 1, % debuff strength
  duration: number;       // 3-12, step 1, seconds
  area: number;           // 40-150, step 10, px radius (for area curse)
}

export interface MinionSliders {
  [key: string]: number;
  minionHealth: number;   // 30-200, step 10, HP
  minionDamage: number;   // 5-30, step 1, flat damage
  manaReserve: number;    // 10-50, step 5, flat mana (NOT percentage!)
}

// Union type for all slider types
export type TypeSpecificSliders =
  | ProjectileSliders
  | AOESliders
  | MeleeSliders
  | BeamSliders
  | AuraSliders
  | BuffSliders
  | TrapSliders
  | TotemSliders
  | CurseSliders
  | MinionSliders;

// ========================
// Slider Configuration
// ========================

export interface SliderConfig {
  key: string;
  label: string;
  labelKey: string;
  min: number;
  max: number;
  step: number;
  default: number;
  color: string;
  unit?: string;  // Display unit (%, px, s, adet, etc.)
}

export const DELIVERY_TYPE_SLIDERS: Record<SkillDeliveryType, SliderConfig[]> = {
  projectile: [
    { key: 'damage', label: 'Hasar', labelKey: 'slider.damage', min: 30, max: 150, step: 5, default: 80, color: '#e74c3c', unit: '%' },
    { key: 'projectileCount', label: 'Mermi Sayısı', labelKey: 'slider.projectileCount', min: 1, max: 7, step: 1, default: 1, color: '#3498db', unit: 'adet' },
    { key: 'speed', label: 'Hız', labelKey: 'slider.speed', min: 200, max: 600, step: 50, default: 400, color: '#f39c12', unit: 'px/s' },
    { key: 'spreadAngle', label: 'Yayılma Açısı', labelKey: 'slider.spreadAngle', min: 0, max: 360, step: 15, default: 0, color: '#1abc9c', unit: '°' },
    { key: 'range', label: 'Menzil', labelKey: 'slider.range', min: 100, max: 400, step: 25, default: 200, color: '#2ecc71', unit: 'px' },
    { key: 'thickness', label: 'Kalınlık', labelKey: 'slider.thickness', min: 5, max: 25, step: 5, default: 10, color: '#9b59b6', unit: 'px' },
    { key: 'pierce', label: 'Delme', labelKey: 'slider.pierce', min: 1, max: 5, step: 1, default: 1, color: '#9b59b6', unit: 'adet' },
    { key: 'chain', label: 'Zincir', labelKey: 'slider.chain', min: 1, max: 5, step: 1, default: 1, color: '#1abc9c', unit: 'adet' },
    { key: 'forkCount', label: 'Çatal Sayısı', labelKey: 'slider.forkCount', min: 2, max: 5, step: 1, default: 2, color: '#e67e22', unit: 'adet' },
  ],
  aoe: [
    { key: 'damage', label: 'Hasar', labelKey: 'slider.damage', min: 10, max: 60, step: 1, default: 25, color: '#e74c3c', unit: 'flat' },
    { key: 'radius', label: 'Yarıçap', labelKey: 'slider.radius', min: 40, max: 200, step: 10, default: 80, color: '#9b59b6', unit: 'px' },
    { key: 'castTime', label: 'Cast Süresi', labelKey: 'slider.castTime', min: 0.3, max: 2.0, step: 0.1, default: 0.8, color: '#f1c40f', unit: 's' },
    { key: 'duration', label: 'Süre', labelKey: 'slider.duration', min: 1, max: 5, step: 0.5, default: 2, color: '#2ecc71', unit: 's' },
    { key: 'coneAngle', label: 'Koni Açısı', labelKey: 'slider.coneAngle', min: 30, max: 120, step: 10, default: 60, color: '#f39c12', unit: '°' },
    { key: 'delay', label: 'Gecikme', labelKey: 'slider.delay', min: 0.5, max: 2.0, step: 0.25, default: 1.0, color: '#f39c12', unit: 's' },
    { key: 'range', label: 'Menzil', labelKey: 'slider.range', min: 50, max: 300, step: 25, default: 150, color: '#2ecc71', unit: 'px' },
  ],
  melee: [
    { key: 'damage', label: 'Hasar', labelKey: 'slider.damage', min: 50, max: 200, step: 5, default: 100, color: '#e74c3c', unit: '%' },
    { key: 'range', label: 'Menzil', labelKey: 'slider.range', min: 30, max: 80, step: 5, default: 50, color: '#2ecc71', unit: 'px' },
    { key: 'thrustRange', label: 'Hamle Menzili', labelKey: 'slider.thrustRange', min: 60, max: 250, step: 10, default: 120, color: '#f39c12', unit: 'px' },
  ],
  beam: [
    { key: 'dps', label: 'DPS', labelKey: 'slider.dps', min: 20, max: 200, step: 5, default: 80, color: '#e74c3c', unit: 'hasar/s' },
    { key: 'width', label: 'Genişlik', labelKey: 'slider.width', min: 5, max: 30, step: 5, default: 15, color: '#9b59b6', unit: 'px' },
    { key: 'range', label: 'Menzil', labelKey: 'slider.range', min: 80, max: 300, step: 20, default: 160, color: '#2ecc71', unit: 'px' },
    { key: 'manaDrain', label: 'Mana Tüketimi', labelKey: 'slider.manaDrain', min: 5, max: 25, step: 1, default: 12, color: '#3498db', unit: 'mana/s' },
  ],
  aura: [
    { key: 'effectStrength', label: 'Etki Gücü', labelKey: 'slider.effectStrength', min: 5, max: 25, step: 1, default: 10, color: '#2ecc71', unit: '%' },
    { key: 'manaReserve', label: 'Mana Rezerv', labelKey: 'slider.manaReserve', min: 15, max: 50, step: 5, default: 25, color: '#3498db', unit: '%' },
  ],
  buff: [
    { key: 'castTime', label: 'Cast Süresi', labelKey: 'slider.castTime', min: 0.3, max: 2.0, step: 0.1, default: 0.6, color: '#f1c40f', unit: 's' },
    { key: 'effectStrength', label: 'Etki Gücü', labelKey: 'slider.effectStrength', min: 30, max: 100, step: 10, default: 50, color: '#f39c12', unit: '' },
    { key: 'charges', label: 'Şarj', labelKey: 'slider.charges', min: 3, max: 8, step: 1, default: 5, color: '#3498db', unit: 'adet' },
    { key: 'hpCost', label: 'HP Maliyeti', labelKey: 'slider.hpCost', min: 1, max: 5, step: 0.5, default: 2, color: '#e74c3c', unit: 'HP/s' },
    { key: 'duration', label: 'Süre', labelKey: 'slider.duration', min: 4, max: 10, step: 1, default: 6, color: '#2ecc71', unit: 's' },
    { key: 'shield', label: 'Kalkan', labelKey: 'slider.shield', min: 50, max: 300, step: 25, default: 100, color: '#9b59b6', unit: 'HP' },
    { key: 'shieldDecay', label: 'Erime Hızı', labelKey: 'slider.shieldDecay', min: 10, max: 30, step: 5, default: 15, color: '#e74c3c', unit: '/s' },
    { key: 'leech', label: 'Yaşam Çalma', labelKey: 'slider.leech', min: 3, max: 15, step: 1, default: 5, color: '#27ae60', unit: '%' },
    { key: 'cooldown', label: 'Bekleme', labelKey: 'slider.cooldown', min: 8, max: 25, step: 1, default: 12, color: '#e74c3c', unit: 's' },
  ],
  trap: [
    { key: 'castTime', label: 'Cast Süresi', labelKey: 'slider.castTime', min: 0.2, max: 1.5, step: 0.1, default: 0.5, color: '#f1c40f', unit: 's' },
    { key: 'damage', label: 'Hasar', labelKey: 'slider.damage', min: 10, max: 80, step: 1, default: 30, color: '#e74c3c', unit: 'flat' },
    { key: 'triggerRadius', label: 'Tetik Yarıçapı', labelKey: 'slider.triggerRadius', min: 20, max: 80, step: 5, default: 40, color: '#9b59b6', unit: 'px' },
    { key: 'trapCount', label: 'Tuzak Sayısı', labelKey: 'slider.trapCount', min: 1, max: 5, step: 1, default: 2, color: '#3498db', unit: 'adet' },
    { key: 'armTime', label: 'Zamanlayıcı', labelKey: 'slider.armTime', min: 0.5, max: 3.0, step: 0.5, default: 1.0, color: '#f39c12', unit: 's' },
  ],
  totem: [
    { key: 'castTime', label: 'Cast Süresi', labelKey: 'slider.castTime', min: 0.3, max: 2.0, step: 0.1, default: 1.0, color: '#f1c40f', unit: 's' },
    { key: 'totemHealth', label: 'Totem Canı', labelKey: 'slider.totemHealth', min: 50, max: 300, step: 25, default: 150, color: '#27ae60', unit: 'HP' },
    { key: 'damage', label: 'Hasar', labelKey: 'slider.damage', min: 5, max: 30, step: 1, default: 15, color: '#e74c3c', unit: 'flat' },
    { key: 'range', label: 'Menzil', labelKey: 'slider.range', min: 60, max: 200, step: 10, default: 120, color: '#2ecc71', unit: 'px' },
  ],
  curse: [
    { key: 'castTime', label: 'Cast Süresi', labelKey: 'slider.castTime', min: 0.3, max: 1.5, step: 0.1, default: 0.6, color: '#f1c40f', unit: 's' },
    { key: 'power', label: 'Lanet Gücü', labelKey: 'slider.power', min: 5, max: 30, step: 1, default: 10, color: '#8e44ad', unit: '%' },
    { key: 'duration', label: 'Süre', labelKey: 'slider.duration', min: 3, max: 12, step: 1, default: 5, color: '#2ecc71', unit: 's' },
    { key: 'area', label: 'Alan', labelKey: 'slider.area', min: 40, max: 150, step: 10, default: 60, color: '#9b59b6', unit: 'px' },
  ],
  minion: [
    { key: 'minionHealth', label: 'Takipçi Canı', labelKey: 'slider.minionHealth', min: 30, max: 200, step: 10, default: 80, color: '#27ae60', unit: 'HP' },
    { key: 'minionDamage', label: 'Takipçi Hasarı', labelKey: 'slider.minionDamage', min: 5, max: 30, step: 1, default: 10, color: '#e74c3c', unit: 'flat' },
    { key: 'manaReserve', label: 'Mana Rezerv', labelKey: 'slider.manaReserve', min: 10, max: 50, step: 5, default: 20, color: '#3498db', unit: 'mana' },
  ],
};

// ========================
// Default Slider Values
// ========================

export function getDefaultSliders(deliveryType: SkillDeliveryType): TypeSpecificSliders {
  switch (deliveryType) {
    case 'projectile':
      return { damage: 80, projectileCount: 1, speed: 400, spreadAngle: 0, range: 200, thickness: 10, pierce: 1, chain: 1, forkCount: 2 };
    case 'aoe':
      return { damage: 25, radius: 80, castTime: 0.8, duration: 2, coneAngle: 60, delay: 1.0, range: 150, shape: 'circle' as AOEShape };
    case 'melee':
      return { damage: 100, range: 50, thrustRange: 120 };
    case 'beam':
      return { dps: 80, width: 15, range: 160, manaDrain: 12 };
    case 'aura':
      return { effectStrength: 10, manaReserve: 25 };
    case 'buff':
      return { castTime: 0.6, effectStrength: 50, charges: 5, hpCost: 2, duration: 6, shield: 100, shieldDecay: 15, leech: 5, cooldown: 12 };
    case 'trap':
      return { castTime: 0.5, damage: 30, triggerRadius: 40, trapCount: 2, armTime: 1.0 };
    case 'totem':
      return { castTime: 1.0, totemHealth: 150, damage: 15, range: 120 };
    case 'curse':
      return { castTime: 0.6, power: 10, duration: 5, area: 60 };
    case 'minion':
      return { minionHealth: 80, minionDamage: 10, manaReserve: 20 };
  }
}

// ========================
// Base Parameters per Delivery Type
// ========================

export interface DeliveryBaseParams {
  baseManaCost: number;
  baseCooldown: number;
  baseManaDrain?: number;   // For beam (mana/sec)
  baseManaReserve?: number; // For aura (% of max mana)
}

export const DELIVERY_BASE_PARAMS: Record<SkillDeliveryType, DeliveryBaseParams> = {
  projectile: { baseManaCost: 8, baseCooldown: 0.6 },
  aoe: { baseManaCost: 25, baseCooldown: 3.5 },       // AOE: 20-40 mana, 3-5s CD
  melee: { baseManaCost: 4, baseCooldown: 0.5 },       // Melee: 0.5s base CD
  beam: { baseManaCost: 0, baseCooldown: 0, baseManaDrain: 12 }, // Lower drain for ~60-70% melee DPS
  aura: { baseManaCost: 0, baseCooldown: 0.5, baseManaReserve: 20 }, // Aura: 0.5s toggle CD (prevent spam)
  buff: { baseManaCost: 18, baseCooldown: 12.0 },      // Buff: 10-15s CD
  trap: { baseManaCost: 14, baseCooldown: 1.5 },
  totem: { baseManaCost: 30, baseCooldown: 10.0 },
  curse: { baseManaCost: 15, baseCooldown: 1.0 },
  minion: { baseManaCost: 0, baseCooldown: 0.5, baseManaReserve: 0 }, // minion uses flat reserve from slider, not %
};

// ========================
// Element Info
// ========================

export interface ElementInfo {
  id: ElementType;
  name: string;
  nameKey: string;
  icon: string;
  color: string;
}

export const ELEMENT_INFO: Record<ElementType, ElementInfo> = {
  physical: {
    id: 'physical',
    name: 'Fiziksel',
    nameKey: 'elements.physical',
    icon: '⚔️',
    color: '#7f8c8d',
  },
  od: {
    id: 'od',
    name: 'Od',
    nameKey: 'elements.od',
    icon: '🔥',
    color: '#e74c3c',
  },
  gok: {
    id: 'gok',
    name: 'Gök',
    nameKey: 'elements.gok',
    icon: '⚡',
    color: '#3498db',
  },
  su: {
    id: 'su',
    name: 'Su',
    nameKey: 'elements.su',
    icon: '❄️',
    color: '#00bcd4',
  },
  karanlik: {
    id: 'karanlik',
    name: 'Karanlık',
    nameKey: 'elements.karanlik',
    icon: '💀',
    color: '#9b59b6',
  },
};

// Array of elements for UI iteration
export const ELEMENTS: ElementType[] = ['physical', 'od', 'gok', 'su', 'karanlik'];

// Array of delivery types for UI iteration
export const DELIVERY_TYPES: SkillDeliveryType[] = [
  'projectile', 'aoe', 'melee', 'beam', 'aura', 'buff', 'trap', 'totem', 'curse', 'minion'
];
